import { Printer, Download } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import DisclosureQuickStatsBar from './DisclosureQuickStatsBar';
import type { EventDisclosureReport } from '../../types/disclosure';

function fmt(ts: string | null | undefined) {
  if (!ts) return '—';
  return formatDateTime(ts);
}

function formTypeLabel(formType: string) {
  if (formType === 'minor_injury') return 'Minor Injury';
  if (formType === 'paracetamol_screening') return 'Paracetamol Screening';
  return 'Patient Report Form';
}

function resourceTypeLabel(type: string | null | undefined) {
  if (!type) return '—';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

interface EventDisclosureReportViewProps {
  report: EventDisclosureReport;
  showActions?: boolean;
  onPrint?: () => void;
  onDownload?: () => void;
}

export default function EventDisclosureReportView({
  report,
  showActions = true,
  onPrint,
  onDownload,
}: EventDisclosureReportViewProps) {
  const { event, resources, incidents, summary } = report;

  return (
    <div className="disclosure-report max-w-4xl mx-auto">
      {showActions && (
        <div className="no-print flex items-center gap-2 mb-6">
          <button
            type="button"
            onClick={onPrint}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
          >
            <Printer className="w-3.5 h-3.5" />
            Print
          </button>
          <button
            type="button"
            onClick={onDownload}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-accent"
          >
            <Download className="w-3.5 h-3.5" />
            Download JSON
          </button>
        </div>
      )}

      <header className="border-b border-border pb-4 mb-6">
        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
          Event Disclosure Report
        </p>
        <h1 className="text-xl font-bold mt-1">{event.name}</h1>
        <p className="text-xs font-mono text-muted-foreground mt-0.5">{event.id}</p>
        <p className="text-[11px] text-muted-foreground mt-2">
          Generated {fmt(report.generatedAt)}
        </p>
      </header>

      <DisclosureQuickStatsBar report={report} />

      <section className="mb-8">
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Event Details
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Category</dt>
            <dd>{event.category}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Status</dt>
            <dd className="uppercase">{event.status}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event date</dt>
            <dd>{event.eventDate || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Start time</dt>
            <dd>{event.eventStartTime || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event opened</dt>
            <dd>{fmt(event.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event closed</dt>
            <dd>{fmt(event.closedAt)}</dd>
          </div>
          {event.closeReason && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Close reason</dt>
              <dd>{event.closeReason}</dd>
            </div>
          )}
          {event.location?.address && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Location</dt>
              <dd>{event.location.address}</dd>
            </div>
          )}
          {event.description && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Description</dt>
              <dd className="whitespace-pre-wrap">{event.description}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Resources ({summary.resourceCount})
        </h2>
        {resources.length === 0 ? (
          <p className="text-sm text-muted-foreground">No resources recorded for this event.</p>
        ) : (
          <table className="w-full text-xs border-collapse disclosure-table">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-3">Callsign</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2">Department</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono">{r.callsign}</td>
                  <td className="py-2 pr-3">{resourceTypeLabel(r.type)}</td>
                  <td className="py-2">{r.department || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Incidents ({summary.incidentCount})
        </h2>
        {incidents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No incidents recorded for this event.</p>
        ) : (
          <div className="space-y-8">
            {incidents.map((inc) => (
              <article key={inc.id} className="border border-border rounded-lg p-4 break-inside-avoid">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
                  <h3 className="font-mono font-bold text-sm">{inc.id}</h3>
                  <span className="text-sm">{inc.title}</span>
                  <span className="text-[10px] uppercase text-muted-foreground ml-auto">{inc.status}</span>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-4">
                  <div>
                    <dt className="text-[10px] uppercase text-muted-foreground">Opened</dt>
                    <dd>{fmt(inc.reportedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-muted-foreground">Closed</dt>
                    <dd>{fmt(inc.closedAt)}</dd>
                  </div>
                  {inc.closeResult && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Result</dt>
                      <dd>{inc.closeResult}</dd>
                    </div>
                  )}
                  {inc.location?.address && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Location</dt>
                      <dd>{inc.location.address}</dd>
                    </div>
                  )}
                </dl>

                {inc.assignments.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      Resource assignments
                    </h4>
                    <table className="w-full text-[11px] border-collapse disclosure-table">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-1.5 pr-2">Callsign</th>
                          <th className="py-1.5 pr-2">Type</th>
                          <th className="py-1.5 pr-2">Assigned</th>
                          <th className="py-1.5 pr-2">En route</th>
                          <th className="py-1.5 pr-2">On scene</th>
                          <th className="py-1.5">Cleared</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inc.assignments.map((a) => (
                          <tr key={a.callsign} className="border-b border-border/50">
                            <td className="py-1.5 pr-2 font-mono">{a.callsign}</td>
                            <td className="py-1.5 pr-2">{resourceTypeLabel(a.resourceType)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.assignedAt)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.enRouteAt)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.onSceneAt)}</td>
                            <td className="py-1.5 font-mono">{fmt(a.clearedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {inc.prfs.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      PRF references
                    </h4>
                    <div className="space-y-3">
                      {inc.prfs.map((prf) => (
                        <div key={prf.id} className="rounded-md bg-muted/40 p-3 text-xs">
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1.5">
                            <span className="font-mono font-semibold">{prf.id}</span>
                            <span className="text-muted-foreground">{formTypeLabel(prf.formType)}</span>
                            <span className="text-muted-foreground ml-auto">{fmt(prf.submittedAt)}</span>
                          </div>
                          {prf.patientName && (
                            <p>
                              <span className="text-muted-foreground">Patient: </span>
                              {prf.patientName}
                            </p>
                          )}
                          {prf.injuryType && (
                            <p>
                              <span className="text-muted-foreground">Injury / complaint: </span>
                              {prf.injuryType}
                            </p>
                          )}
                          {prf.resultingAction && (
                            <p>
                              <span className="text-muted-foreground">Resulting action: </span>
                              {prf.resultingAction}
                            </p>
                          )}
                          {prf.dealtWithBy.length > 0 && (
                            <p>
                              <span className="text-muted-foreground">Dealt with by: </span>
                              {prf.dealtWithBy.join(', ')}
                            </p>
                          )}
                          <p className="text-muted-foreground mt-1">Submitted by {prf.submittedBy}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {summary.patientNamesRedacted && (
        <p className="mt-6 text-[11px] text-muted-foreground border border-border rounded-lg px-3 py-2">
          Patient names in this shared report are redacted to initials. Full names are available only in
          Command or when a share link was created with “Include full patient names”.
        </p>
      )}

      <footer className="mt-10 pt-4 border-t border-border text-[10px] text-muted-foreground">
        CADSys Command — confidential event disclosure. Contains operational and patient-related data;
        handle in accordance with applicable data protection requirements.
      </footer>
    </div>
  );
}

export function downloadDisclosureJson(report: EventDisclosureReport, filename?: string) {
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `disclosure-${report.event.id}-${report.generatedAt.slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function printDisclosureReport() {
  window.print();
}
